import { memo, useMemo, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import Markdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

type AssistantTextProps = {
  chatMessage: string;
};

const remarkPlugins = [remarkGfm];

const AssistantText = memo(({ chatMessage }: AssistantTextProps) => {
  const components = useMemo(
    () => ({
      code: CodeComponent,
    }),
    [],
  );

  return (
    <Box
      fontSize="16px"
      className="text-fade-in"
      data-testid="aa-message-streaming-text"
      sx={{
        whiteSpace: "normal",
        wordWrap: "break-word",
        "--animation-duration": "300ms",
        "--fade-animation-function": "linear",
        textAlign: "left",
      }}
    >
      <Markdown
        remarkPlugins={remarkPlugins}
        components={components}
      >{`${chatMessage}`}</Markdown>
    </Box>
  );
});

export default AssistantText;

export const CodeComponent = (props: any) => {
  const { children, className, node, ...rest } = props;
  const match = /language-(\w+)/.exec(className || "");
  const codeString = String(children).replace(/\n$/, "");

  if (!match) {
    return (
      <code
        {...rest}
        className={className}
        style={{
          backgroundColor: "#e0e0e0",
        }}
      >
        {children}
      </code>
    );
  }

  return (
    <Box sx={{ position: "relative", "&:hover .copy-btn": { opacity: 1 } }}>
      <CopyCodeButton code={codeString} />
      {/* @ts-ignore */}
      <SyntaxHighlighter
        {...rest}
        PreTag="div"
        children={codeString}
        language={match[1]}
        style={atomDark}
        wrapLines={true}
        wrapLongLines={true}
      />
    </Box>
  );
};

const CopyCodeButton = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // Clipboard API failed (non-secure context, permission denied, etc.)
      },
    );
  };

  return (
    <Tooltip title={copied ? "Copied!" : "Copy code"}>
      <IconButton
        className="copy-btn"
        size="small"
        onClick={handleCopy}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          opacity: 0,
          transition: "opacity 0.2s",
          color: "#ccc",
          bgcolor: "rgba(255,255,255,0.1)",
          "&:hover": {
            bgcolor: "rgba(255,255,255,0.2)",
          },
        }}
      >
        {copied ? (
          <CheckIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};
